import React, { useEffect, useState } from 'react';
import { useNewSurgerie } from '../../hooks/useNewSurgerie';
import { InputAdornment, Radio, styled, TextField, Typography } from '@mui/material';
import useSearch from 'hooks/search';
import { ProcedureDoctor } from 'types/procedure';
import { moneyFormat } from 'helpers/moneyFormat';
import NoData from 'components/no-data/NoData';
import { usePermissionRules } from 'hooks/permissionRules';
import {
  CardBody,
  GuidedCard,
  GuidedHeader,
  palette,
  PriceBox,
  PriceRange,
  SelectionCard,
  SelectionContent,
} from '../../components/GuidedSurgerie';
import ChargedValue from '../../components/ChargedValue';
import { SearchOutlined } from '@mui/icons-material';

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
}));

const DoctorMeta = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 260px',
  gap: 12,
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const SurgerieThirdStep: React.FC = () => {
  const { surgerie, handleChange, doctors } = useNewSurgerie();
  const [value, setValue] = useState('');
  const { checkPermission } = usePermissionRules();
  const [filtered, setFiltered] = useState<ProcedureDoctor[]>([]);
  const search = useSearch();

  useEffect(() => {
    setFiltered(doctors);
  }, [doctors]);

  useEffect(() => {
    if (value) {
      const _rules = search(value, 'name', doctors);
      setFiltered(_rules);
      return;
    }

    setFiltered(doctors);
  }, [value, doctors]);

  return (
    <GuidedCard>
      <GuidedHeader
        step={3}
        title="Médico"
        description="Escolha o médico responsável e ajuste o valor quando sua permissão permitir."
      />
      <CardBody>
        <TextField
          value={value}
          onChange={e => setValue(e.target.value)}
          label="Informe o médico"
          placeholder="Digite o nome do médico"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />

        {filtered.length === 0 ? (
          <NoData message="Nenhum médico encontrado" />
        ) : (
          <Container>
            {filtered.map(doctor => {
              const selected = surgerie.doctor_id === doctor.id;
              return (
                <SelectionCard
                  selected={selected}
                  key={doctor.id}
                  onClick={() => {
                    handleChange('doctor_id', doctor.id);
                    handleChange('doctor', doctor);
                  }}
                >
                  <Radio checked={selected} value={doctor.id} />
                  <SelectionContent>
                    <DoctorMeta>
                      <div>
                        <Typography variant="body2" fontWeight={700} color={palette.ink}>
                          {doctor.doctor?.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Sugerido para {surgerie.procedure?.name || 'o procedimento selecionado'}
                        </Typography>
                      </div>

                      {checkPermission('surgeries.new.change-doctor-value') && selected && (
                        <ChargedValue
                          title="Editar valor do médico"
                          value={surgerie.value_doctor}
                          min={doctor.sale_price_min}
                          max={doctor.sale_price_max}
                          onChange={v => handleChange('value_doctor', v)}
                        />
                      )}
                    </DoctorMeta>

                    <PriceRange>
                      <PriceBox>
                        <Typography variant="caption" color="textSecondary">
                          Preço mínimo
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="#b42318">
                          {moneyFormat(doctor.sale_price_min)}
                        </Typography>
                      </PriceBox>
                      <PriceBox>
                        <Typography variant="caption" color="textSecondary">
                          Preço máximo sugerido
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="success.main">
                          {moneyFormat(doctor.sale_price_max)}
                        </Typography>
                      </PriceBox>
                    </PriceRange>
                  </SelectionContent>
                </SelectionCard>
              );
            })}
          </Container>
        )}
      </CardBody>
    </GuidedCard>
  );
};

export default SurgerieThirdStep;
